import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { ArchiveTimelineRow, StationRemark } from "../../api/archive";

// Per-year coverage: one thick row per year with a 12-month mini chart
// (pages as bars, hours as an area) and a red tick under any month that's
// missing content — either absent from the index or present-but-partial
// (missing_days > 0). Page/hour totals are summed to the year. Computed
// entirely from the per-month `timeline`, so it tracks live data with no
// backend change. (Per-year Programs/Sources are distinct counts that
// can't be summed from per-month data — those need a backend by_year
// rollup; deliberately left out here.)

interface MonthCell {
  pages: number;
  hours: number;
  partial: boolean; // in the index but missing_days > 0
}

interface YearAgg {
  year: string;
  pages: number;
  hours: number;
  missing: number; // months in-coverage that are absent or partial
  cells: (MonthCell | null)[]; // index 0..11 → Jan..Dec
  cov: boolean[]; // is month i within the archive's overall coverage span
}

const monthIdx = (m: string): number => {
  const [y, mo] = m.split("-").map(Number);
  return y * 12 + (mo - 1);
};

const compact = (n: number): string =>
  new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(
    Math.round(n),
  );

function aggregate(rows: ArchiveTimelineRow[]): {
  years: YearAgg[];
  maxPages: number;
  maxHours: number;
} {
  if (rows.length === 0) return { years: [], maxPages: 1, maxHours: 1 };
  const firstIdx = monthIdx(rows[0].month);
  const lastIdx = monthIdx(rows[rows.length - 1].month);
  const byYear = new Map<string, (MonthCell | null)[]>();
  let maxPages = 1;
  let maxHours = 1;

  for (const r of rows) {
    const [y, m] = r.month.split("-");
    const arr = byYear.get(y) ?? Array.from({ length: 12 }, () => null as MonthCell | null);
    arr[Number(m) - 1] = {
      pages: r.pages,
      hours: r.duration_hours,
      partial: (r.missing_days ?? 0) > 0,
    };
    byYear.set(y, arr);
    maxPages = Math.max(maxPages, r.pages);
    maxHours = Math.max(maxHours, r.duration_hours);
  }

  const years = [...byYear.keys()]
    .sort((a, b) => b.localeCompare(a)) // newest first
    .map((year): YearAgg => {
      const cells = byYear.get(year) as (MonthCell | null)[];
      const cov = cells.map((_, i) => {
        const idx = Number(year) * 12 + i;
        return idx >= firstIdx && idx <= lastIdx;
      });
      let pages = 0;
      let hours = 0;
      let missing = 0;
      cells.forEach((c, i) => {
        if (c) {
          pages += c.pages;
          hours += c.hours;
          if (cov[i] && c.partial) missing += 1;
        } else if (cov[i]) {
          missing += 1; // absent within coverage = a gap
        }
      });
      return { year, pages, hours, missing, cells, cov };
    });

  return { years, maxPages, maxHours };
}

// Bucket arrival/departure remarks by their year (remark.month is "YYYY-MM").
function countByYear(remarks: StationRemark[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of remarks) {
    const y = (r.month ?? "").slice(0, 4);
    if (y) m.set(y, (m.get(y) ?? 0) + 1);
  }
  return m;
}

export function YearCoverageTable({
  data,
  arrivals,
  departures,
  onYearClick,
}: {
  data: ArchiveTimelineRow[];
  arrivals: StationRemark[];
  departures: StationRemark[];
  onYearClick?: (year: string) => void;
}) {
  const { years, maxPages, maxHours } = aggregate(data);
  const addedBy = countByYear(arrivals);
  const decomBy = countByYear(departures);
  if (years.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No coverage yet.
      </Typography>
    );
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>year</TableCell>
          <TableCell>coverage · bars = pages · area = hours (own scale)</TableCell>
          <TableCell align="right">pages</TableCell>
          <TableCell align="right">hours</TableCell>
          <TableCell align="right">added</TableCell>
          <TableCell align="right">decommissioned</TableCell>
          <TableCell align="right">gaps</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {years.map((y) => (
          <TableRow
            key={y.year}
            hover
            onClick={onYearClick ? () => onYearClick(y.year) : undefined}
            sx={onYearClick ? { cursor: "pointer" } : undefined}
          >
            <TableCell sx={{ verticalAlign: "middle" }}>
              <Typography variant="h6">{y.year}</Typography>
            </TableCell>
            <TableCell sx={{ py: 1 }}>
              <MonthMiniChart year={y} maxPages={maxPages} maxHours={maxHours} />
            </TableCell>
            <TableCell align="right">{compact(y.pages)}</TableCell>
            <TableCell align="right">{compact(y.hours)}</TableCell>
            <TableCell align="right">
              {(addedBy.get(y.year) ?? 0) > 0 ? (
                <Typography variant="body2" color="success.main">
                  +{addedBy.get(y.year)}
                </Typography>
              ) : (
                "—"
              )}
            </TableCell>
            <TableCell align="right">
              {(decomBy.get(y.year) ?? 0) > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  −{decomBy.get(y.year)}
                </Typography>
              ) : (
                "—"
              )}
            </TableCell>
            <TableCell align="right">
              {y.missing > 0 ? (
                <Typography variant="body2" color="error">
                  {y.missing} mo
                </Typography>
              ) : (
                "—"
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const MONTHS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

function MonthMiniChart({
  year,
  maxPages,
  maxHours,
}: {
  year: YearAgg;
  maxPages: number;
  maxHours: number;
}) {
  const theme = useTheme();
  const W = 300;
  const H = 72;
  const padX = 4;
  const top = 6;
  const baseY = 52;
  const barAreaH = baseY - top;
  const labelY = H - 4;
  const slotW = (W - 2 * padX) / 12;
  const barW = Math.min(14, slotW - 6);

  const cx = (i: number) => padX + (i + 0.5) * slotW;
  const hoursY = (c: MonthCell | null) =>
    c ? baseY - (c.hours / maxHours) * barAreaH : baseY;

  // Hours area polygon across all 12 slots (dips to baseline at gaps).
  const areaPts = year.cells.map((c, i) => `${cx(i).toFixed(1)},${hoursY(c).toFixed(1)}`).join(" ");
  const areaPath = `${padX},${baseY} ${areaPts} ${W - padX},${baseY}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H}>
      {/* pages (document count) as bars — the primary layer */}
      {year.cells.map((c, i) => {
        if (!c) return null;
        const h = (c.pages / maxPages) * barAreaH;
        return (
          <rect
            key={i}
            x={cx(i) - barW / 2}
            y={baseY - h}
            width={barW}
            height={h}
            fill={theme.palette.primary.main}
          >
            <title>{`${year.year}-${String(i + 1).padStart(2, "0")}: ${c.pages.toLocaleString()} pages, ${Math.round(c.hours).toLocaleString()} hours${c.partial ? " (partial)" : ""}`}</title>
          </rect>
        );
      })}
      {/* baseline */}
      <line x1={padX} y1={baseY} x2={W - padX} y2={baseY} stroke={theme.palette.divider} strokeWidth={1} />
      {/* hours as a transparent area overlay on its OWN y-scale, on top of
          the bars + a connecting line so it reads as one continuous area
          rather than interleaved bars. */}
      <polygon points={areaPath} fill={theme.palette.secondary.main} fillOpacity={0.15} />
      <polyline
        points={areaPts}
        fill="none"
        stroke={theme.palette.secondary.main}
        strokeWidth={1.5}
        strokeOpacity={0.9}
      />
      {/* red marks under missing-content months + month initials */}
      {year.cells.map((c, i) => {
        const missingContent = year.cov[i] && (c === null || c.partial);
        return (
          <g key={`m-${i}`}>
            {missingContent && (
              <rect x={cx(i) - 4} y={baseY + 3} width={8} height={3} fill={theme.palette.error.main}>
                <title>{`${year.year}-${String(i + 1).padStart(2, "0")}: ${c === null ? "no data" : "partial month"}`}</title>
              </rect>
            )}
            <text
              x={cx(i)}
              y={labelY}
              fontSize={8}
              fill={theme.palette.text.disabled}
              textAnchor="middle"
            >
              {MONTHS[i]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}